import React, { useEffect, useMemo, useState } from "react";
import { FaSearch, FaFileExcel } from "react-icons/fa";
import * as XLSX from "xlsx";
import { postData } from "../../../services/FetchNodeservices";

export default function DailyClosingReport() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [recruiterFilter, setRecruiterFilter] = useState("");
  const [page, setPage] = useState(1);

  /* DATE FILTER */
  const [dateFilter, setDateFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const rowsPerPage = 12;

  /* ================= FETCH ================= */
  const fetchReport = async () => {
    const payload = { dateFilter, fromDate, toDate };
    const res = await postData("daily-closing-report/fetch", payload);
    if (res?.status) setRows(res.data || []);
  };

  useEffect(() => {
    fetchReport();
  }, [dateFilter, fromDate, toDate]);

  /* ================= HELPERS ================= */
  const normalize = (v) =>
    String(v ?? "").replace(/\s+/g, " ").trim().toLowerCase();

  const parseDate = (d) => {
    if (!d) return null;
    const date = new Date(d);
    return isNaN(date) ? null : date;
  };

  const isSameDay = (d1, d2) =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  /* ================= FILTERING ================= */
  const filtered = useMemo(() => {
    const today = new Date();

    return rows.filter((r) => {
      const term = normalize(search);
      const matchSearch =
        normalize(r.req_id).includes(term) ||
        normalize(r.job_title).includes(term);

      const matchRecruiter =
        recruiterFilter === "" ||
        normalize(r.recruiter_name) === normalize(recruiterFilter);

      let matchDate = true;
      const rowDate = parseDate(r.uploaded_date);

      if (dateFilter !== "all" && !rowDate) return false;

      if (dateFilter === "today") matchDate = isSameDay(rowDate, today);

      if (dateFilter === "yesterday") {
        const y = new Date(today);
        y.setDate(today.getDate() - 1);
        matchDate = isSameDay(rowDate, y);
      }

      if (dateFilter === "week") {
        const weekAgo = new Date(today);
        weekAgo.setDate(today.getDate() - 7);
        matchDate = rowDate >= weekAgo;
      }

      if (dateFilter === "custom" && fromDate && toDate) {
        const from = new Date(fromDate);
        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999);
        matchDate = rowDate >= from && rowDate <= to;
      }

      return matchSearch && matchRecruiter && matchDate;
    });
  }, [rows, search, recruiterFilter, dateFilter, fromDate, toDate]);

  /* ================= PAGINATION ================= */
  const pageCount = Math.ceil(filtered.length / rowsPerPage) || 1;
  const displayed = filtered.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  /* ================= TOTALS ================= */
  const totals = useMemo(() => {
    return filtered.reduce(
      (t, r) => ({
        sourced: t.sourced + (r.candidates_sourced || 0),
        submitted: t.submitted + (r.profiles_submitted || 0),
        interviews: t.interviews + (r.interviews_scheduled || 0),
        offers: t.offers + (r.offers || 0),
        joins: t.joins + (r.joins || 0),
      }),
      { sourced: 0, submitted: 0, interviews: 0, offers: 0, joins: 0 }
    );
  }, [filtered]);

  /* ================= EXPORT ================= */
  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filtered);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Daily Closing");
    XLSX.writeFile(wb, "Daily_Closing_Report.xlsx");
  };

  /* ================= RECRUITERS ================= */
  const recruiters = useMemo(() => {
    return [...new Set(rows.map((r) => r?.recruiter_name).filter(Boolean))].sort();
  }, [rows]);

  /* ================= UI ================= */
  return (
    <div className="md:ml-72 px-4 md:px-6 pt-24 pb-8 min-h-screen text-slate-800">

      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-5">
        <h2 className="text-lg md:text-xl font-bold text-gray-700">
          Daily Closing Report
        </h2>

        <button
          onClick={exportExcel}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm rounded-lg w-fit"
        >
          <FaFileExcel /> Export
        </button>
      </div>

      {/* FILTERS */}
      <div className="glass-card p-3 sm:p-4 mb-4 flex flex-col sm:flex-row flex-wrap gap-3 items-start">
        {/* Search */}
        <div className="relative w-full sm:w-64">
          <FaSearch className="absolute top-3 left-3 text-gray-400 text-sm" />
          <input
            type="text"
            placeholder="Search Req ID / Job..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="glass-input pl-10 pr-3 py-2 rounded-xl outline-none text-sm"
          />
        </div>

        {/* Date Filter */}
        <select
          value={dateFilter}
          onChange={(e) => {
            setDateFilter(e.target.value);
            setPage(1);
          }}
          className="glass-input px-3 py-2 text-sm rounded-xl outline-none w-full sm:w-auto"
        >
          <option value="all">All</option>
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="week">Last Week</option>
          <option value="custom">Custom</option>
        </select>

        {dateFilter === "custom" && (
          <>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="glass-input px-3 py-2 text-sm rounded-xl outline-none w-full sm:w-auto"
            />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="glass-input px-3 py-2 text-sm rounded-xl outline-none w-full sm:w-auto"
            />
          </>
        )}

        {/* Recruiter */}
        <select
          value={recruiterFilter}
          onChange={(e) => {
            setRecruiterFilter(e.target.value);
            setPage(1);
          }}
          className="glass-input px-3 py-2 text-sm rounded-xl outline-none w-full sm:w-auto"
        >
          <option value="">All Recruiters</option>
          {recruiters.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>

      {/* TABLE */}
      <div className="glass-card overflow-x-auto">
        <table className="min-w-[1100px] w-full text-sm whitespace-nowrap">
          <thead>
            <tr className="bg-slate-50/75 border-b border-slate-250 text-slate-500 font-semibold text-[11px] uppercase tracking-wider">
              <th className="px-6 py-3.5 text-center">Sr</th>
              <th className="px-6 py-3.5 text-left">Date</th>
              <th className="px-6 py-3.5 text-left">Recruiter</th>
              <th className="px-6 py-3.5 text-left">Job Title</th>
              <th className="px-6 py-3.5 text-left">Req ID</th>
              <th className="px-6 py-3.5 text-center">Vac</th>
              <th className="px-6 py-3.5 text-center">Sourced</th>
              <th className="px-6 py-3.5 text-center">Submitted</th>
              <th className="px-6 py-3.5 text-center">Interviews</th>
              <th className="px-6 py-3.5 text-center">Offers</th>
              <th className="px-6 py-3.5 text-center">Joins</th>
              <th className="px-6 py-3.5 text-left">Remarks</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100">
            {displayed.length === 0 ? (
              <tr>
                <td colSpan={12} className="px-6 py-6 text-center text-slate-500">
                  No data found
                </td>
              </tr>
            ) : (
              displayed.map((r, i) => (
                <tr key={`${r.req_id}-${i}`} className="hover:bg-slate-50/50 transition-colors duration-150">
                  <td className="px-6 py-4 text-center text-slate-500">
                    {(page - 1) * rowsPerPage + i + 1}
                  </td>
                  <td className="p-3">
                    {r.uploaded_date
                      ? new Date(r.uploaded_date).toLocaleDateString()
                      : "-"}
                  </td>
                  <td className="px-6 py-4 text-slate-650">{r.recruiter_name}</td>
                  <td className="px-6 py-4 text-slate-700 font-medium">{r.job_title}</td>
                  <td className="px-6 py-4 text-slate-500 font-mono">{r.req_id}</td>
                  <td className="px-6 py-4 text-center text-slate-650">{r.vacancies}</td>
                  <td className="px-6 py-4 text-center text-slate-650">{r.candidates_sourced}</td>
                  <td className="px-6 py-4 text-center text-slate-650">{r.profiles_submitted}</td>
                  <td className="px-6 py-4 text-center text-slate-650">{r.interviews_scheduled}</td>
                  <td className="px-6 py-4 text-center text-slate-650">{r.offers}</td>
                  <td className="px-6 py-4 text-center text-slate-650">{r.joins}</td>
                  <td className="px-6 py-4 text-slate-600 max-w-xs truncate">{r.remarks || "-"}</td>
                </tr>
              ))
            )}

            {/* TOTALS */}
            <tr className="bg-slate-50 font-bold text-slate-700 border-t border-slate-200">
              <td colSpan={6} className="px-6 py-3.5 text-right uppercase tracking-wider text-xs">
                TOTAL
              </td>
              <td className="px-6 py-3.5 text-center">{totals.sourced}</td>
              <td className="px-6 py-3.5 text-center">{totals.submitted}</td>
              <td className="px-6 py-3.5 text-center">{totals.interviews}</td>
              <td className="px-6 py-3.5 text-center">{totals.offers}</td>
              <td className="px-6 py-3.5 text-center">{totals.joins}</td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      <div className="flex justify-between items-center mt-4">
        <button
          disabled={page === 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          className="glass-btn px-4 py-1.5 rounded-xl text-xs font-semibold disabled:opacity-50 disabled:pointer-events-none"
        >
          Prev
        </button>

        <span className="text-xs font-medium text-slate-500">
          Page {page} of {pageCount}
        </span>

        <button
          disabled={page === pageCount}
          onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
          className="glass-btn px-4 py-1.5 rounded-xl text-xs font-semibold disabled:opacity-50 disabled:pointer-events-none"
        >
          Next
        </button>
      </div>
    </div>
  );
}
